import { setLocale } from 'yup'
import { ru } from 'yup-locales'
import { PageLink, PageTitle } from '../../../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IMessageSender, IRequisiteType } from '../../core/response.types'
import { getMessageSenderList, getRequisiteTypeList } from '../../core/api'
import ReactPaginate from 'react-paginate'
import { MessageSenderListQuery } from '../../core/query.types'
import { DEFAULT_MESSAGE_SENDER_QUERY, DEFAULT_REQUISITE_TYPE_QUERY } from '../../core/query.defaults'
import { serializeFiltersToUrl } from '../../../../../../../utils/statusFiltersURL'
import { Table } from '../../../../../../../components/Table'
import { TableHead } from '../../../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../../../components/Table/TableBody'
import { LoaderForTable } from '../../../../../../../components/Loader'
import MessageSenderItem from './MessageSenderItem'
import ModalNewMessageSender from '../common/ModalNewMessageSender'
import { ApplyFiltersArgs } from '../../core/types'
import { useLocation } from 'react-router-dom'
import { MESSAGE_BREADCRUMBS } from '../../core/consts'

setLocale(ru)


export default () => {
    const location = useLocation()
    const state = location.state as {
        initialSender?: string;
    } | null;

    const initialSender = state?.initialSender ?? '';
    
    const breadCrumbs = useMemo<PageLink[]>(
        () => MESSAGE_BREADCRUMBS.map((b) => ({
            ...b,
            isActive: location.pathname === b.path,
        })),
        [location.pathname]
    )

    const [initialized, setInitialized] = useState(false)
    const [isInitializing, setInitializing] = useState(false)
    const [isLoading, setLoading] = useState(false)
    const [senders, setSenders] = useState<IMessageSender[]>([])
    const [requisiteTypes, setRequisiteTypes] = useState<IRequisiteType[]>([])
    const [senderCount, setSenderCount] = useState(0)
    const [pageCount, setPageCount] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [query, setQuery] = useState<MessageSenderListQuery>(DEFAULT_MESSAGE_SENDER_QUERY)

    const initializPage = async () => {
        setInitializing(true)

        try {
            const response = await getRequisiteTypeList(DEFAULT_REQUISITE_TYPE_QUERY)
            if (!response?.data?.success) {
                throw new Error(
                    'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
                )
            }

            const list = response.data.data ?? []

            setRequisiteTypes(list)
        } catch (error) {
            console.error('Ошибка загрузки справочника:', error)
            setRequisiteTypes([])
        } finally {
            setInitializing(false)
        }
    }

    const loadMessageSenderList = useCallback(
        async (params: MessageSenderListQuery) => {
            setLoading(true)
            
            try {
                const response = await getMessageSenderList(params)
                if (!response?.data?.success) {
                    throw new Error(
                        'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
                    )
                }

                const list = response.data.data || []
                const totalRows = response.data.totalRows || 0
                const pages = Math.ceil(totalRows / params.perPage)

                setSenders(list)
                setSenderCount(totalRows)
                setPageCount(pages)
            } catch (error) {
                console.error('Ошибка загрузки шаблонов:', error)
                setSenders([])
                setSenderCount(0)
                setPageCount(0)
            } finally {
                setLoading(false)
            }
        },
        []
    )

    const applyFilters = useCallback(
        ({ patch, page }: ApplyFiltersArgs = {}) => {
            
            const nextQuery: MessageSenderListQuery = {
                ...DEFAULT_MESSAGE_SENDER_QUERY,
                ...query,
                ...patch,
                page: page ?? patch?.page ?? query.page,
            }

            setQuery(nextQuery)
    
            const urlQuery = serializeFiltersToUrl(nextQuery, DEFAULT_MESSAGE_SENDER_QUERY)
            const nextUrl = urlQuery ? `${window.location.pathname}?${urlQuery}` : window.location.pathname
            window.history.replaceState({}, '', nextUrl)
    
          loadMessageSenderList(nextQuery)
        },
        [query, loadMessageSenderList]
    )

    // Добавляем нового отправителя в конец таблицы
    const handleSaveSender = useCallback(
        (newSender: IMessageSender) => {            
            setSenders((prev) => [...prev, newSender])
            setSenderCount((prev) => prev + 1)            
        },
        [query, loadMessageSenderList]
    )

    useEffect(() => {
        if (initialized) return

        const init = async () => {
            await initializPage()
            applyFilters()
            setInitialized(true)
        }

        init()
    }, [initialized, applyFilters])

    useEffect(() => {
        if (!initialized) return

        if (location.hash === '#newSender') {
            setIsModalOpen(true)
        } else {
            setIsModalOpen(false)
        }
    }, [location.hash, initialized])
    
    return (
        <div>
            <div className='card sticky-top-tickets mb-7'>
                <PageTitle breadcrumbs={breadCrumbs}>Отправители</PageTitle>
                <div className='card-header border-0 pt-5 pb-2'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bold fs-3 mb-1'>Отправители сообщений</span>
                        <span className="text-muted mt-1 fw-semibold fs-7">Всего отправителей: {isLoading || isInitializing ? <span className='spinner-border spinner-border-sm align-middle'></span> : senderCount}</span>
                    </h3>
                    <div className="card-toolbar" style={{cursor: isLoading || isInitializing ? 'not-allowed' : 'pointer'}}>
                        <button 
                            className="btn btn-sm btn-primary"                            
                            disabled={isLoading || isInitializing}
                            onClick={() => setIsModalOpen(true)}
                        >
                            <KTIcon iconName="plus" className="fs-2" />
                            Добавить отправителя
                        </button>
                    </div>
                </div>
            </div>
            <div className="card">
                <div className="card-body py-3">
                    <Table>
                        <TableHead>
                            <tr>
                                <th className='min-w-40px'>ID</th>
                                <th className='min-w-150px'>Отправитель</th>
                                <th className='min-w-100px'>Тип реквизита</th>
                                <th className='min-w-100px'>Способ доставки</th>
                                <th className='min-w-150px'>Дата создания</th>
                                <th className='min-w-100px'></th>
                            </tr>
                        </TableHead>
                        <TableBody>              
                            {!isLoading && !isInitializing && senders.length > 0 ? (
                                senders.map((sender, key) => (
                                    <MessageSenderItem
                                        key={sender.id}
                                        sender={sender}
                                    />
                                ))
                            ) : (
                                <LoaderForTable isLoading={isLoading || isInitializing} colSpan={6} />
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
            {pageCount > 1 && (
                <div className='d-flex justify-content-center mt-5'>
                    <ReactPaginate
                        breakLabel='...'
                        nextLabel='Следующая >'
                        onPageChange={(selected) =>
                            applyFilters({ page: selected.selected + 1 })
                        }
                        pageRangeDisplayed={5}
                        pageCount={pageCount}
                        previousLabel='< Предыдущая'
                        renderOnZeroPageCount={() => null}
                        className='pagination'
                        activeClassName='active'
                        disabledClassName='disabled'
                        nextClassName='page-item'
                        nextLinkClassName='page-link'
                        pageClassName='page-item'
                        pageLinkClassName='page-link'
                        previousClassName='page-item'
                        previousLinkClassName='page-link'
                        breakClassName='page-item'
                        breakLinkClassName='page-link'
                    />
                </div>
            )}
            {isModalOpen && 
                <ModalNewMessageSender 
                    show={isModalOpen} 
                    onClose={setIsModalOpen} 
                    requisiteTypes={requisiteTypes} 
                    onSave={handleSaveSender}
                    initialSender={initialSender}
                />
            }
        </div>
    )
}
